import React, { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { ArrowLeft, RefreshCw, ShieldAlert } from "lucide-react";

const CARDS = [
  ["affected_files", "Berørte filer"],
  ["affected_users", "Berørte brukere"],
  ["private", "Privat"],
  ["friends", "Venner"],
  ["vip", "VIP"],
  ["paid", "Med pris"],
  ["tier_gated", "Krever nivå"],
  ["purchased", "Kjøpt av noen"],
];

const Card = ({ id, label, value }) => (
  <div className="panel-tight p-4 border border-white/10" data-testid={`legacy-card-${id}`}>
    <div className="text-[10px] uppercase tracking-widest text-[var(--cream-soft)]">{label}</div>
    <div className="font-fraunces italic text-3xl text-[var(--cream)] mt-1">{value ?? 0}</div>
  </div>
);

const UserRow = ({ u }) => (
  <tr className="border-b border-white/5 align-top" data-testid={`legacy-user-${u.username}`}>
    <td className="p-2">
      <Link to={`/profile/${u.username}`} className="text-[var(--magenta)] hover:underline">@{u.username}</Link>
      {u.is_creator && <span className="ml-1 text-[10px] text-amber-300">skaper</span>}
    </td>
    <td className="p-2">{u.total}</td>
    <td className="p-2">{u.private}</td>
    <td className="p-2">{u.friends}</td>
    <td className="p-2">{u.vip}</td>
    <td className="p-2">{u.paid}</td>
    <td className="p-2">{u.tier_gated}</td>
    <td className="p-2">{u.purchased}</td>
    <td className="p-2 text-[var(--cream-soft)]">
      {u.albums.map((a) => (
        <div key={a.album_id || "none"}>
          {a.album_id ? <Link to={`/albums/${a.album_id}`} className="hover:underline">{a.title}</Link> : a.title}
          {" "}({a.files}{a.pool === "creator" ? ", skaper" : ""})
        </div>
      ))}
    </td>
  </tr>
);

export default function AdminGamleAlbumFiler() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const run = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/admin/media/legacy-visibility");
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Klarte ikke kjøre sjekken.");
    } finally {
      setLoading(false);
    }
  };

  const c = data ? { ...data.counts, affected_files: data.affected_files, affected_users: data.affected_users } : {};

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="admin-legacy-visibility-page">
        <Link to="/admin/board" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1 mb-3" data-testid="back-to-admin">
          <ArrowLeft className="h-3.5 w-3.5" /> Admin-oversikt
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert className="h-6 w-6 text-[var(--magenta)]" />
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">Gamle album-filer</h1>
        </div>
        <p className="text-[var(--cream-soft)] text-sm mb-6 max-w-3xl">
          Filer lastet opp før v220 der eier har valgt noe annet enn «Offentlig», satt pris eller krav om nivå.
          For disse sjekker serveren ikke valget i dag. Sjekken leser bare data og endrer ingenting.
        </p>

        <button onClick={run} disabled={loading} className="btn-magenta inline-flex items-center gap-2 mb-6" data-testid="legacy-run-check-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Sjekker …" : "Sjekk gamle album-filer"}
        </button>

        {err && <div className="p-4 mb-4 text-red-400 text-sm border border-red-400/20 bg-red-500/5" data-testid="legacy-error">{err}</div>}

        {data && (
          <>
            <p className="text-xs text-[var(--cream-soft)] mb-3" data-testid="legacy-total">
              Gamle filer totalt: {data.total_legacy_files} · Bilder {data.counts.images} · Filmer {data.counts.videos} (blant berørte) · Kjørt {new Date(data.generated_at).toLocaleString("nb-NO")}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {CARDS.map(([id, label]) => <Card key={id} id={id} label={label} value={c[id]} />)}
            </div>
            <div className="panel-tight overflow-x-auto">
              <table className="w-full text-xs" data-testid="legacy-users-table">
                <thead>
                  <tr className="text-left uppercase tracking-widest text-[var(--cream-soft)] border-b border-white/10 text-[10px]">
                    <th className="p-2">Bruker</th><th className="p-2">Totalt</th><th className="p-2">Privat</th>
                    <th className="p-2">Venner</th><th className="p-2">VIP</th><th className="p-2">Pris</th>
                    <th className="p-2">Nivå</th><th className="p-2">Kjøpt</th><th className="p-2">Album (filer)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.length === 0
                    ? <tr><td colSpan={9} className="p-6 text-center text-[var(--cream-soft)]" data-testid="legacy-empty">Ingen berørte filer.</td></tr>
                    : data.users.map((u) => <UserRow key={u.user_id} u={u} />)}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
